import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ComputedPageItem} from '@taopix/taopix-core/dist/types/types';
import {Button, SwapPicturesIcon} from '@taopix/taopix-design-system';
import {AssetType, BoxType, NumBool} from '@taopix/taopix-core';
import {useTaopix} from '../../../Context/TaopixContext';
import classNames from 'classnames';
import {useExperience} from '../../../Experience/ExperienceProvider';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';

export type SwapPicturesProps = {
  sectionKey: string;
  item?: ComputedPageItem;
};

const SwapPictures = ({sectionKey, item}: SwapPicturesProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const {t} = useTranslation();

  const {experience} = useExperience();
  const {pictureBoxOptions} = experience.EDITOR;

  const [showButton, setShowButton] = useState<boolean>(false);
  const [disableButton, setDisableButton] = useState<boolean>(false);
  const [, setNotAvailableMessage] = useState<string>();

  const swapToggled = useRef<boolean>(false);

  const isActiveInExperience = (item: ComputedPageItem) => {
    const boxType = item.type;
    // Return whether or not this option is enabled in the experience for the selected item
    if (
      boxType === BoxType.Image &&
      item?.asset?.assetType !== AssetType.Background &&
      item?.asset?.assetType !== AssetType.ScrapbookImage
    ) {
      return pictureBoxOptions.swapPictures;
    } else {
      return false;
    }
  };

  useEffect(() => {
    // If there is a selected item
    if (item && item.ref !== -1) {
      // Show or hide this section based on the relevant experience and advanced editing configuration
      setShowButton(isActiveInExperience(item));
      // Enable or disable this section based on the item's protection
      setDisableButton(
        NumBool.True === item?.protectcontents || (item.errors?.imageMissing as boolean)
      );
      // Set the message to 'not available' in case the section was hidden or disabled
      setNotAvailableMessage(t('toolpalette:str_OptionNotAvailableForSelectedItem'));
    } else {
      // If no item is selected, hide the section button
      setShowButton(false);
      // And show the 'please select an item' message
      setNotAvailableMessage(t('toolpalette:str_MessageSelectAnItemToViewOptions'));
    }
  }, [item?.asset, item?.asset?.assetType, item?.type, item?.image, item?.shape, item?.ref]);

  const buttonClassName = classNames('px-xs', 'shrink-0', 'w-full');

  useEffect(() => {
    // if item is deselected due to scroll ect, toggle show overlay off
    if (item?.ref === -1) {
      app.core.dispatch('SHAPE.TOGGLE_SWAP', {show: false}).catch(error => {
        console.error(error);
      });
    }
  }, [item?.ref]);

  if (
    ui.state.palette.section !== 'box-settings-palette' &&
    ui.state.palette.section !== `box-settings-palette/${sectionKey}`
  ) {
    return null;
  }

  return showButton ? (
    <Button
      data-testid={'box-settings-swappictures-button'}
      label={t('boxpalettes:str_TitleSwapPictures')}
      className={buttonClassName}
      startIcon={<SwapPicturesIcon />}
      size={ui.state.isSmallScreen ? 'large' : 'medium'}
      buttonStyle={'tertiary'}
      labelLines={3}
      aria-pressed={ui.state.swapActive}
      onClick={() => {
        if (!ui.state.swapActive) {
          app.core.dispatch('SHAPE.TOGGLE_SWAP', {show: true}).catch(error => {
            console.error(error);
          });
        } else {
          app.core.dispatch('SHAPE.TOGGLE_SWAP', {show: false}).catch(error => {
            console.error(error);
          });
        }
      }}
      disabled={disableButton}
    ></Button>
  ) : (
    <></>
  );
};

export default SwapPictures;
